import { Box, Stack, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';

import {
    mobilePaymentRowAmountBadgeSx,
    mobilePaymentRowBodySx,
    mobilePaymentRowCardSx,
    mobilePaymentRowChevronSx,
    bankAccountSelectSx,
} from '../../pages/dashboard/DashboardStyles';
import { formatCurrency } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';
import { CustomCheckBox } from '../checkBox/CustomCheckBox';
import CommonDatePicker, { DatePickerValidationReason } from '../date-picker/CommonDatePicker';
import { CustomSelect } from '../select/CustomSelect';
import { colors } from '../theme/theme';

import { PaymentAmountCell } from './PaymentAmountCell';
import { PaymentRowNotices } from './PaymentRowNotices';
import { RowAutoPayAction } from './RowAutoPayAction';

import type { CardFeeByRowEntry } from './PaymentRowNotices';
import type { BankAccountOption } from '../../hooks/make-payment/useContractPaymentData';
import type { AmountChoice, PaymentMethodType, PaymentRow } from '../../hooks/make-payment/usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';
import type { SavedPaymentMethodDisplay } from '../modals/AddPaymentMethodModal';
import type { Dispatch, SetStateAction } from 'react';

interface MobilePaymentContractCardProps {
    contractId: string;
    /** The contract itself, so autopay status can show even when the card has no payment row (unselected). */
    contractData?: ContractApiResponse | null;
    isSelectable: boolean;
    tooltipMessage: string;
    row: PaymentRow | null;
    rowIndex: number | null;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onToggleSelect: () => void;
    paymentMethodType: PaymentMethodType;
    isStripeEnabled?: boolean;
    bankAccountOptions: BankAccountOption[];
    savedPaymentMethods: SavedPaymentMethodDisplay[];
    openInvoices: OpenInvoiceApiResponse[];
    resolveContractForPaymentRow: (row: PaymentRow) => ContractApiResponse | null;
    isConsumerLoanContract: (contract: ContractApiResponse | null | undefined) => boolean;
    getEffectivePaymentAmount: (row: PaymentRow, contract: ContractApiResponse | null | undefined) => number;
    cardFeeEntry: CardFeeByRowEntry | undefined;
    cardFeesLoading?: boolean;
    getRowCardFeeAmount: (row: PaymentRow, rowIndex: number) => number;
    paymentRowError: { bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean } | undefined;
    paymentSubmitError?: string | null;
    paymentDatePickerError: string | undefined;
    handleBankAccountChange: (index: number, value: string) => void;
    handlePaymentCardChange: (index: number, value: string) => void;
    handleDateChange: (index: number, value: dayjs.Dayjs | null) => void;
    handleDatePickerError: (index: number, reason: DatePickerValidationReason, meta?: { rawInput: string }) => void;
    handleAmountChoiceChange: (index: number, choice: AmountChoice) => void;
    setOtherAmountInputRef: (index: number, el: HTMLInputElement | null) => void;
    setPaymentRows: Dispatch<SetStateAction<PaymentRow[]>>;
    setPaymentRowErrors: Dispatch<
        SetStateAction<Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>>
    >;
    setPaymentSubmitErrors: Dispatch<SetStateAction<Array<string | null>>>;
}

const hasContractInvoices = (contractId: string, openInvoices: OpenInvoiceApiResponse[]): boolean => {
    const searchId = String(contractId).trim().toUpperCase();
    return openInvoices.some((inv: OpenInvoiceApiResponse) => {
        const aspireId = String(inv?.AspireContractId || inv?.aspireContractId || '')
            .trim()
            .toUpperCase();
        const dbId = String(inv?.contractId || inv?.ContractId || '')
            .trim()
            .toUpperCase();
        return aspireId === searchId || dbId === searchId;
    });
};

/**
 * Mobile-only accordion card for a single contract: checkbox + contractId header (with the due
 * amount badge once selected), expanding to the same bank/card select, date picker, amount cell,
 * and notices used by the desktop `PaymentRowsTable`.
 */
export const MobilePaymentContractCard: React.FC<MobilePaymentContractCardProps> = ({
    contractId,
    contractData,
    isSelectable,
    tooltipMessage,
    row,
    rowIndex,
    isExpanded,
    onToggleExpand,
    onToggleSelect,
    paymentMethodType,
    isStripeEnabled = false,
    bankAccountOptions,
    savedPaymentMethods,
    openInvoices,
    resolveContractForPaymentRow,
    isConsumerLoanContract,
    getEffectivePaymentAmount,
    cardFeeEntry,
    cardFeesLoading,
    getRowCardFeeAmount,
    paymentRowError,
    paymentSubmitError,
    paymentDatePickerError,
    handleBankAccountChange,
    handlePaymentCardChange,
    handleDateChange,
    handleDatePickerError,
    handleAmountChoiceChange,
    setOtherAmountInputRef,
    setPaymentRows,
    setPaymentRowErrors,
    setPaymentSubmitErrors,
}) => {
    const { t } = useTranslation();
    const isSelected = row !== null && rowIndex !== null;
    const contract = row ? resolveContractForPaymentRow(row) : (contractData ?? null);
    const outstandingBalance =
        contract?.OutstandingBalance != null
            ? Number(contract.OutstandingBalance)
            : contract?.RemainingPrincipal != null
              ? Number(contract.RemainingPrincipal)
              : null;

    const savedCardOptions = savedPaymentMethods
        .filter(pm => {
            if (pm.type !== 'card') return false;
            if (!pm.expiryMonth || !pm.expiryYear) return true;
            const expiryDate = new Date(Number(pm.expiryYear), Number(pm.expiryMonth), 0, 23, 59, 59, 999);
            return expiryDate >= new Date();
        })
        .map(pm => ({ id: pm.id, name: pm.label }));
    const selectedCard = savedPaymentMethods.find(pm => pm.id === row?.paymentCardId);

    const hasInvoicesForRow = row ? hasContractInvoices(row.contractId, openInvoices) : false;
    const amountChoice = row?.amountChoice ?? 'invoice';
    // Commercial (non-consumer-loan) bank-transfer contracts fall back to the outstanding balance
    // when there are no open invoices (see PaymentAmountCell's showsOutstandingBalanceFallback),
    // so their badge must still count that amount rather than showing $0.
    const showsOutstandingBalanceFallback = !isConsumerLoanContract(contract) && paymentMethodType !== 'card';
    // Mirror DashboardMakePayment's totalAmount guard: a row with amountChoice 'invoice' and no
    // actual open invoices contributes $0 to the footer total, so the badge must show $0 too —
    // otherwise it falls back to the contract's full outstanding balance (see getEffectivePaymentAmount)
    // and no longer sums to the displayed Subtotal/Total.
    const effectiveAmount =
        row && (amountChoice !== 'invoice' || hasInvoicesForRow || showsOutstandingBalanceFallback)
            ? getEffectivePaymentAmount(row, contract ?? undefined)
            : 0;

    const handleHeaderClick = (e?: React.MouseEvent) => {
        if (!isSelectable) {
            e?.preventDefault();
            e?.stopPropagation();
            return;
        }
        if (isSelected) {
            onToggleExpand();
        } else {
            onToggleSelect();
        }
    };

    return (
        <Tooltip title={tooltipMessage} arrow placement="bottom">
            <Box
                sx={{
                    ...mobilePaymentRowCardSx(isExpanded),
                    p: 1.5,
                    opacity: isSelectable ? 1 : 0.7,
                    pointerEvents: isSelectable ? 'auto' : 'none',
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ cursor: isSelectable ? 'pointer' : 'not-allowed' }}
                    onClick={handleHeaderClick}
                >
                    <CustomCheckBox
                        disabled={!isSelectable}
                        checked={isSelectable && isSelected}
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            if (isSelectable) onToggleSelect();
                        }}
                        slotProps={{ input: { 'aria-label': `Select contract ${contractId}` } }}
                        sx={{ '& svg': { height: '16px', width: '16px' } }}
                    />
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color={isSelectable ? colors.fullBlack : colors.greyPreset[150]}
                        sx={{ minWidth: 0, flexShrink: 0 }}
                    >
                        {contractId}
                    </Typography>
                    {/* Autopay-enabled contracts in the "not available" block are already greyed out and explained by the divider note. */}
                    {!(contract?.AutoPay && !isSelectable) && <RowAutoPayAction contract={contract} compact />}
                    <Box sx={{ flex: 1 }} />
                    {isSelected && (
                        <Box sx={mobilePaymentRowAmountBadgeSx}>
                            <Typography variant="body2" noWrap>
                                {formatCurrency(effectiveAmount)}
                            </Typography>
                        </Box>
                    )}
                    {isSelected && (
                        <span
                            className="icon-arrow-drop-down"
                            style={mobilePaymentRowChevronSx(isExpanded) as React.CSSProperties}
                        />
                    )}
                </Stack>

                {isSelected && isExpanded && row && rowIndex !== null && (
                    <Stack sx={mobilePaymentRowBodySx}>
                        {paymentMethodType === 'card' ? (
                            <CustomSelect
                                id={`payment-card-select-mobile-${rowIndex}`}
                                ariaLabel={t('makePayment.paymentCard')}
                                labelSrOnly
                                value={row.paymentCardId}
                                onChange={value => handlePaymentCardChange(rowIndex, value)}
                                options={savedCardOptions}
                                placeholder={
                                    savedCardOptions.length === 0
                                        ? t('makePayment.noSavedCardsClickAdd')
                                        : t('makePayment.selectPaymentCard')
                                }
                                noOptionsMessage={t('makePayment.noSavedCards')}
                                sx={bankAccountSelectSx}
                                error={!!paymentRowError?.bankAccount}
                            />
                        ) : (
                            <CustomSelect
                                id={`bank-account-select-mobile-${rowIndex}`}
                                ariaLabel={t('payments.createPayment.paymentAccount')}
                                labelSrOnly
                                value={row.bankAccount}
                                onChange={value => handleBankAccountChange(rowIndex, value)}
                                options={bankAccountOptions}
                                placeholder={
                                    bankAccountOptions.length === 0
                                        ? t('payments.createPayment.noBankAccountsPresent')
                                        : t('payments.createPayment.selectBankAccount')
                                }
                                noOptionsMessage={t('payments.createPayment.noBankAccountsPresent')}
                                sx={bankAccountSelectSx}
                                error={!!paymentRowError?.bankAccount}
                            />
                        )}

                        {!(isStripeEnabled && paymentMethodType === 'card') && (
                            <CommonDatePicker
                                value={row.paymentDate}
                                onChange={value => handleDateChange(rowIndex, value)}
                                onPickerError={(reason, _v, meta) => handleDatePickerError(rowIndex, reason, meta)}
                                DatePickerErrorValue={paymentDatePickerError}
                                calenderIconColor={colors.greyPreset[80]}
                                minDate={dayjs().startOf('day')}
                                maxDate={
                                    paymentMethodType === 'card'
                                        ? dayjs().startOf('day')
                                        : (contract?.MaturityDate ? dayjs(contract.MaturityDate) : dayjs())
                                              .add(90, 'day')
                                              .startOf('day')
                                }
                                ariaLabel={`Payment date row ${rowIndex + 1}`}
                                error={!!paymentRowError?.paymentDate}
                            />
                        )}

                        <PaymentAmountCell
                            row={row}
                            rowIndex={rowIndex}
                            paymentMethodType={paymentMethodType}
                            isConsumerLoan={isConsumerLoanContract(contract)}
                            outstandingBalance={outstandingBalance}
                            hasContractInvoices={hasInvoicesForRow}
                            getRowCardFeeAmount={getRowCardFeeAmount}
                            paymentAmountError={paymentRowError?.paymentAmount}
                            cardFeesLoading={cardFeesLoading}
                            selectedCardFunding={selectedCard?.funding}
                            selectedCardCountry={selectedCard?.country}
                            setPaymentRows={setPaymentRows}
                            setPaymentRowErrors={setPaymentRowErrors}
                            setPaymentSubmitErrors={setPaymentSubmitErrors}
                            handleAmountChoiceChange={handleAmountChoiceChange}
                            setOtherAmountInputRef={setOtherAmountInputRef}
                        />

                        <PaymentRowNotices
                            row={row}
                            contract={contract}
                            paymentMethodType={paymentMethodType}
                            cardFeeEntry={cardFeeEntry}
                            submitError={paymentSubmitError}
                        />
                    </Stack>
                )}
            </Box>
        </Tooltip>
    );
};
